// Production-Ready Timeline Viewer
// Features: Significance filtering, interactive vertical timeline, expandable events, export

import React, { useState, useMemo } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileText,
  FileImage,
  Filter,
  Tag,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Timeline, TimelineEvent } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

type SignificanceFilter = 'all' | 'major' | 'minor' | 'contextual';

interface TimelineViewerProps {
  timeline: Timeline;
  className?: string;
}

const SIGNIFICANCE_CONFIG: Record<
  TimelineEvent['significance'],
  { size: string; colour: string; ring: string; label: string }
> = {
  major: {
    size: 'w-5 h-5',
    colour: 'bg-indigo-600',
    ring: 'ring-4 ring-indigo-600/20',
    label: 'Major',
  },
  minor: {
    size: 'w-3.5 h-3.5',
    colour: 'bg-indigo-400',
    ring: '',
    label: 'Minor',
  },
  contextual: {
    size: 'w-2.5 h-2.5',
    colour: 'bg-indigo-300',
    ring: '',
    label: 'Contextual',
  },
};

const FILTER_OPTIONS: { value: SignificanceFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'major', label: 'Major' },
  { value: 'minor', label: 'Minor' },
  { value: 'contextual', label: 'Contextual' },
];

export function TimelineViewer({ timeline, className = '' }: TimelineViewerProps) {
  const [activeFilter, setActiveFilter] = useState<SignificanceFilter>('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  const filteredEvents = useMemo(() => {
    if (!timeline?.events?.length) return [];
    if (activeFilter === 'all') return timeline.events;
    return timeline.events.filter((e) => e.significance === activeFilter);
  }, [timeline?.events, activeFilter]);

  const toggleEvent = (id: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalEvents = timeline?.events?.length ?? 0;
  const shownEvents = filteredEvents.length;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Clock className="h-5 w-5 text-indigo-600 shrink-0" />
            <h2 className="text-xl font-bold text-foreground leading-tight">
              {timeline.title}
            </h2>
            <Badge variant="secondary" className="shrink-0">
              {totalEvents} event{totalEvents !== 1 ? 's' : ''}
            </Badge>
          </div>
          {timeline.description && (
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              {timeline.description}
            </p>
          )}
          {(timeline.startDate || timeline.endDate) && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {timeline.startDate}
                {timeline.startDate && timeline.endDate && ' — '}
                {timeline.endDate}
              </span>
            </div>
          )}
        </div>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={isExporting} className="shrink-0">
              <Download className="h-4 w-4 mr-1.5" />
              {isExporting ? 'Exporting…' : 'Export'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportToPdf('timeline', timeline)}>
              <FileImage className="h-4 w-4 mr-2" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportToDocx('timeline', timeline)}>
              <FileText className="h-4 w-4 mr-2" />
              Export as Word (.docx)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Significance filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
        {FILTER_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant={activeFilter === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter(option.value)}
            className={cn(
              'h-7 text-xs transition-all',
              activeFilter === option.value && 'bg-indigo-600 hover:bg-indigo-700'
            )}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {/* Event count */}
      <p className="text-xs text-muted-foreground">
        Showing {shownEvents} of {totalEvents} event{totalEvents !== 1 ? 's' : ''}
      </p>

      {/* Timeline */}
      <ScrollArea className="max-h-[600px] pr-2">
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Clock className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No events match the selected filter.</p>
          </div>
        ) : (
          <div className="relative pl-4 sm:pl-0">
            {filteredEvents.map((event, index) => {
              const config = SIGNIFICANCE_CONFIG[event.significance];
              const isExpanded = expandedEvents.has(event.id);
              const isLast = index === filteredEvents.length - 1;

              return (
                <div
                  key={event.id}
                  className="group relative flex gap-4 sm:gap-6 pb-6 last:pb-0"
                >
                  {/* Left column: date/period label */}
                  <div className="hidden sm:flex w-28 shrink-0 flex-col items-end pt-1 text-right">
                    {event.date && (
                      <span className="text-xs font-semibold text-foreground leading-tight">
                        {event.date}
                      </span>
                    )}
                    {event.period && (
                      <span className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                        {event.period}
                      </span>
                    )}
                  </div>

                  {/* Vertical line and node */}
                  <div className="relative flex flex-col items-center shrink-0">
                    {/* Node */}
                    <div
                      className={cn(
                        'relative z-10 rounded-full shrink-0 transition-transform group-hover:scale-110',
                        config.size,
                        config.colour,
                        config.ring
                      )}
                    />
                    {/* Connecting line */}
                    {!isLast && (
                      <div className="absolute top-5 left-1/2 -translate-x-1/2 w-0.5 bg-indigo-200 dark:bg-indigo-800 h-full" />
                    )}
                  </div>

                  {/* Right column: event card */}
                  <div className="flex-1 min-w-0 pb-2">
                    {/* Mobile date display */}
                    <div className="sm:hidden mb-1">
                      {event.date && (
                        <span className="text-xs font-semibold text-muted-foreground">
                          {event.date}
                        </span>
                      )}
                      {event.period && (
                        <span className="text-[11px] text-muted-foreground ml-1">
                          {event.period}
                        </span>
                      )}
                    </div>

                    <Card
                      className={cn(
                        'cursor-pointer transition-all duration-200 hover:shadow-md border',
                        index % 2 === 1 && 'bg-muted/30',
                        isExpanded && 'ring-1 ring-indigo-300 dark:ring-indigo-700'
                      )}
                      onClick={() => toggleEvent(event.id)}
                    >
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-sm font-semibold text-foreground leading-tight">
                                {event.title}
                              </h3>
                              <Badge
                                variant={
                                  event.significance === 'major'
                                    ? 'default'
                                    : event.significance === 'minor'
                                    ? 'secondary'
                                    : 'outline'
                                }
                                className={cn(
                                  'text-[10px] h-5',
                                  event.significance === 'major' &&
                                    'bg-indigo-600 hover:bg-indigo-600'
                                )}
                              >
                                {config.label}
                              </Badge>
                              {event.category && (
                                <Badge variant="outline" className="text-[10px] h-5">
                                  <Tag className="h-2.5 w-2.5 mr-1" />
                                  {event.category}
                                </Badge>
                              )}
                            </div>

                            {/* Collapsed: truncated description */}
                            {!isExpanded && (
                              <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                                {event.description}
                              </p>
                            )}
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEvent(event.id);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </div>

                        {/* Expanded: full description */}
                        <div
                          className={cn(
                            'overflow-hidden transition-all duration-300',
                            isExpanded ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'
                          )}
                        >
                          <div className="pt-2 border-t">
                            <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                              {event.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

export default TimelineViewer;
